/**
 * Prints or downloads one document on its own, so a freelancer can keep a copy of what they
 * accepted (or save it as a PDF from the print dialog). The page is opened in a
 * new window holding only the document, because printing the app would print
 * its header and layout too.
 */

const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/**
 * Escapes text for an HTML document.
 * @param {string} text
 * @returns {string}
 */
export function escapeHTML(text) {
    return String(text).replace(/[&<>"']/g, (ch) => ESCAPES[ch]);
}

/**
 * Builds the standalone page printed for a document.
 * @param {{ title: string, details?: string[], bodyHTML: string }} doc
 *   `bodyHTML` is rendered from our own document text, never user input.
 *   `details` are short lines under the title, such as the version and the
 *   date it was accepted.
 * @returns {string}
 */
export function buildPrintHTML({ title, details = [], bodyHTML }) {
    const detailLines = details.map((d) => `<div>${escapeHTML(d)}</div>`).join('');
    return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${escapeHTML(title)}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #222; line-height: 1.5; margin: 2rem auto; max-width: 46rem; padding: 0 1rem; font-size: 11pt; }
  .details { color: #555; font-size: 9.5pt; border-bottom: 1px solid #ccc; padding-bottom: 0.75rem; margin-bottom: 1.25rem; }
  h1 { font-size: 16pt; } h2 { font-size: 13pt; margin-top: 1.25rem; } h3 { font-size: 11.5pt; }
  table { border-collapse: collapse; width: 100%; margin: 0.75rem 0; }
  th, td { border: 1px solid #ccc; padding: 0.35rem 0.5rem; text-align: left; vertical-align: top; }
  @media print { body { margin: 0; max-width: none; } }
</style>
</head>
<body>
<div class="details"><strong>${escapeHTML(title)}</strong>${detailLines}</div>
${bodyHTML}
</body>
</html>`;
}

/**
 * Opens the document in a new window and shows the print dialog.
 * @param {{ title: string, details?: string[], bodyHTML: string }} doc
 */
export function printDocument(doc) {
    const win = window.open('', '_blank');
    if (!win)
        throw new Error(
            'Your browser blocked the print window. Allow pop-ups for this site and try again.',
        );
    win.document.open();
    win.document.write(buildPrintHTML(doc));
    win.document.close();
    win.focus();
    win.print();
}

/**
 * A download file name for a document, e.g. "Privacy Notice v4.html". Only
 * characters every file system accepts are kept.
 * @param {string} title
 * @param {number | string} version
 * @returns {string}
 */
export function documentFileName(title, version) {
    const safe = String(title)
        .replace(/[^A-Za-z0-9 ._-]/g, '')
        .trim();
    return `${safe || 'Document'} v${version}.html`;
}

/**
 * Downloads the document as a standalone page (the one printDocument prints),
 * which opens and prints in any browser.
 * @param {{ title: string, details?: string[], bodyHTML: string }} doc
 * @param {string} fileName
 */
export function downloadDocument(doc, fileName) {
    const url = URL.createObjectURL(new Blob([buildPrintHTML(doc)], { type: 'text/html' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
}
